const _ = require("lodash");
const React = require("react");
const ReactDOM = require("react-dom/client");
const domready = require("domready");
const ReactModal2 = require("react-modal2").default;

const Bundles = require("./Bundles");
const loadBundle = require("./loadBundle");
const BundlePreload = require("./util/BundlePreload");
const Router = require("./Router");
const {parseURL} = require("../../shared/URLParser");
const MockVaultPlugins = require("./vault/MockVaultPlugins");
const SEOCopy = require("../../shared/SEOCopy");
const SharedErrorLog = require("../../shared/SharedErrorLog");
const {
	USE_OLD_SERVER_ERROR_LOGGING,
	VERSION_CHECK_MIN_DELAY,
	VERSION_CHECK_MAX_DELAY,
} = require("../../shared/Constants");
const Vault = require("./vault/util/Vault");
const VaultLogger = require("./vault/util/VaultLogger");
const AT = require("./vault/ActionTypes");
const URLHooks = require("./vault/URLHooks");
const URLMake = require("../../shared/URLMake");
const OpsUser = require("./vault/OpsUser");
const {atUser, atCart, atCurrResult} = require("./vault/StateCursors");
const Updater = require("./vault/util/Updater");
const UpdateCore = require("./vault/UpdateCore");
const API = require("./vault/API");
const Clock = require("./vault/Clock");
const Throttler = require("./vault/Throttler");
const {LEFT_SIDE_NAV_WIDTH} = require("./util/NavigationUtil");
const Storage = require("./util/Storage");
const Cookie = require("./util/Cookie");
const Sounds = require("./util/Sounds");
const {shouldRenderLeftSideNavBar} = require("./util/NavigationUtil");
const ReadAloudContext = require("./util/ReadAloudContext");
const {addDuplicateDispatchFix} = require("./vault/util/DuplicateDispatchFix");
const {
	addMisorderedDispatchFix,
} = require("./vault/util/MisorderedDispatchFix");
const {addPeriodicUpdateFix} = require("./vault/util/PeriodicUpdateFix");
const MainContainer = require("./top/MainContainer");
const ErrorUtil = require("../../shared/ErrorUtil");
const Analytics = require("./util/Analytics");
const Diagnostics = require("./util/Diagnostics");
const BugCopy = require("./util/CopyTable").forBugReport();
const {FLAGS} = require("./util/GlobalFlags");
const WindowResize = require("./util/WindowResize");
const CanvasUtil = require("./util/CanvasUtil");

// The various initialization steps are separated into distinct functions which
// can be called individually if needed. The startReact function below calls
// them all in the order needed by a normal page load.

const createVault = function(options = {}) {
	const {mockPlugins = false} = options;

	const updater = new Updater();
	UpdateCore.augment(updater);
	const vault = new Vault(updater);
	const router = new Router(vault, (pageState, pageOptions) =>
		vault.dispatch({
			type: AT.SET_PAGE,
			pageState,
			fromBrowser: !!pageOptions.fromBrowser,
		})
	);
	const vaultLogger = new VaultLogger(vault);

	if (!process.env.IS_NODE) {
		addDuplicateDispatchFix(vault, vaultLogger);
		addMisorderedDispatchFix(vault, vaultLogger);
		addPeriodicUpdateFix(vault);
	}

	let plugins;
	if (!mockPlugins) {
		const clock = Clock.makeClockForVault(vault);
		const api = API.makeAPIForVault(vault, clock);
		const cookie = Cookie.makeCookieForVault(vault);
		const sounds = Sounds;
		const throttler = new Throttler({
			getNow: () => clock.now(),
		});
		plugins = {
			clock,
			api,
			throttler,
			cookie,
			sounds,
			storage: Storage.getStorage(),
			dispatchAfter: (action, ms) =>
				setTimeout(() => vault.dispatchNow(action), ms),
			reloadPage: x => setTimeout(() => window.location.reload(x), 0),
			redirect: href => setTimeout(() => (window.location = href), 0),
			getDiagnostics: diagOptions => {
				return Diagnostics.get(_.extend(diagOptions, {vaultLogger}));
			},
			// This is mutated and accessed directly (TODO: don't like the impurity
			// of this but the desired functionality is inherently impure...)
			hooks: {},
		};
	} else {
		plugins = MockVaultPlugins.makeMockAll(vault);
	}

	vault.addUpdatePlugin(plugins);

	return {vault, vaultLogger, updater, plugins, router};
};

const setupDebugVars = function(vault, vaultLogger) {
	let consoleReportsLeft = 5;
	global.DEBUG_submitConsoleBugReport = desc => {
		if (!consoleReportsLeft) {
			// Silently fail to prevent abuse
			global.DEBUG_submitConsoleBugReport = () => {};
			return;
		}
		consoleReportsLeft -= 1;
		vault.dispatch({
			type: AT.SEND_BUG_REPORT_REQUEST,
			payload: {
				reporter: 0,
				title: "Console bug report",
				desc: desc,
				baBlocks: false,
				blockID: null,
				screenshot: "",
				type: "teacher",
			},
		});
	};

	if (process.env.NODE_ENV !== "production") {
		global.DEBUG_AT = AT;
		global.DEBUG_ACTION_HISTORY = vaultLogger.actionHistory;
		global.DEBUG_dispatch = action => vault.dispatch(action);
		global.DEBUG_getDiagnostics = diagOptions => {
			return Diagnostics.get(_.extend(diagOptions, {vaultLogger}));
		};
		global.DEBUG_throwMockError = message => {
			setTimeout(() => {
				throw new Error(message || "Mock error");
			}, 0);
		};
		global.DEBUG_logErrorUtil = argument => {
			setTimeout(() => {
				ErrorUtil.log(
					"E_INTENTIONAL",
					"Called DEBUG_logErrorUtil to intentionally log an error",
					{argument}
				);
			}, 0);
		};
		global.DEBUG_rerender = () => vault.dispatch({type: AT.DEBUG_RERENDER});
	}
};

// Need to expose a function throttled in setupErrorTrapping for flushing in
// the unload handler. Bit messy.
let flushErrorSaving = () => {};

const setupErrorTrapping = function(vault, vaultLogger, api) {
	// It is safe to set up both Sentry and the old system simultaneously. The
	// errors will be logged twice and some measures may need to be taken to
	// prevent double emails, but it makes for a safe way of testing Sentry
	// without risk of losing data.

	if (USE_OLD_SERVER_ERROR_LOGGING) {
		let pendingErrors = [];

		const throttledSave = _.throttle(() => {
			const user = _.get(vault, "state.user", {});
			const userID = user && user.userID;
			api.logErrors(userID, pendingErrors, (saveErr, errorList) => {
				if (!saveErr || saveErr === "E_TOO_MANY_REQUESTS") {
					return;
				}
				pendingErrors = pendingErrors.concat(errorList);
				throttledSave();
			});
			pendingErrors = [];
		}, 5000);
		flushErrorSaving = () => throttledSave.flush();

		ErrorUtil.addListenerAndCallForPrevious(obj => {
			if (FLAGS.doNotSendErrorsToServer) {
				return;
			}
			pendingErrors.push(_.clone(obj));
			throttledSave();
		});

		ErrorUtil.setupExceptionTrapper();
	}

	ErrorUtil.setDiagnosticsFunction(() => Diagnostics.get({vaultLogger}));
	SharedErrorLog.setErrorLogger(ErrorUtil.log);
};

const setupVaultUpdate = function(vault, router, api, cookieUtil) {
	setupVaultUpdateRouter(vault, router);
	setupVaultUpdateAnalytics(vault, api, cookieUtil);
	setupVaultUpdateReadAloud(vault);
};

const setupVaultUpdateRouter = function(vault, router) {
	let lastRedirectURL = null;

	vault.on("update", (vaultState, generation, customEvents) => {
		const redirectURL = vaultState.redirectURL;
		const user = vaultState.user;
		if (redirectURL && user && user.authenticated) {
			// Don't run the redirect more than once. Fixes T29181.
			if (lastRedirectURL !== redirectURL) {
				lastRedirectURL = redirectURL;
				setTimeout(() => (global.window.location = redirectURL), 0);
			}
			return;
		} else {
			BundlePreload.getPreloadList(vaultState.page).forEach(bundleName =>
				Bundles.load(bundleName)
			);

			const url = URLMake.forPageState(vaultState.page);
			const replace = !customEvents.filter(
				x => x.name === "addToBrowserHistory"
			).length;
			router.setURL(url, {replace, silent: true});

			const title = SEOCopy.getTitle(vaultState.page);
			if (title) {
				document.title = title;
			}
		}
	});
};

const setupVaultUpdateAnalytics = function(vault, api, cookieUtil) {
	let firstPage = null;
	let onFirstPage = true;
	const loc = global.window && window.location;
	const firstPath = loc && loc.pathname + loc.search;
	vault.on("update", (vaultState, generation, customEvents) => {
		const user = vaultState.user;

		firstPage = firstPage || vaultState.page;
		onFirstPage = onFirstPage && firstPage === vaultState.page;

		const currentPathname = _.get(window, "location.pathname");

		if (user) {
			const isStudent = OpsUser.isStudent(user);
			const isNotStudentOrNotLoggedIn = OpsUser.isNotStudentOrNotLoggedIn(user);
			if (isStudent) {
				// React analytics are only started on trainer pages.
				// For school students, Hotjar is disabled within Analytics.

				if (currentPathname.startsWith("/school")) {
					Analytics.start(api, cookieUtil, user);
				} else {
					Analytics.stop();
				}
			} else if (
				isNotStudentOrNotLoggedIn &&
				!currentPathname.startsWith("/school")
			) {
				Analytics.start(api, cookieUtil, user);
				Analytics.sendPageEvents(
					vaultState.page,
					onFirstPage ? firstPath : null,
					vaultState.cart
				);
			}
		}
	});
};

const initVaultState = function(vault, url) {
	// TODO: clean up and also see initial action handlers, better way?
	const pageState = parseURL(url);
	const preloadedState =
		!process.env.IS_NODE && global.window && global.window.BA_PRELOADED_STATE;
	const isServerSide = !!preloadedState;
	vault.dispatchNow(
		{
			type: AT.INITIAL,
			state: preloadedState || null,
			pageState,
			isServerSide,
		},
		{init: true}
	);
	if (isServerSide) global.window.BA_PRELOADED_STATE = null;
	// Originally added because of T30711. Keeping around post-socket removal
	// since it shouldn't hurt.
	const delay = _.random(VERSION_CHECK_MIN_DELAY, VERSION_CHECK_MAX_DELAY);
	setTimeout(() => {
		vault.dispatchNow({type: AT.VERSION_CHECK_WAITED});
	}, delay);
	return isServerSide;
};

/**
 * Sets up a vault listener that allows managing the speech API declaratively
 * through vault state. This constructs the ReadAloudContext object, updates
 * it according to vault changes in state.readAloud, and sends speech to it
 * via the readAloudSpeak vault event.
 *
 * @param {object} vault
 */

const setupVaultUpdateReadAloud = function(vault) {
	const rac = new ReadAloudContext();
	rac.onStatusChange(newStatus => {
		const action = _.extend({type: AT.UPDATE_READ_ALOUD_STATUS}, newStatus);
		vault.dispatch(action);
	});
	const speakEvents = {
		readAloudSpeak: speakEvent => {
			rac.speak(speakEvent.args[0]);
		},
		readAloudPause: speakEvent => {
			rac.pause();
		},
		readAloudResume: speakEvent => {
			rac.resume();
		},
		readAloudCancel: speakEvent => {
			rac.cancel();
		},
		readAloudPreload: speakEvent => {
			rac.preload(speakEvent.args[0]);
		},
	};

	vault.on("update", (vaultState, generation, customEvents) => {
		const incomingSpeakEvents = _.filter(customEvents, custEvt => {
			return _.has(speakEvents, custEvt.name);
		});

		const toCall = incomingSpeakEvents.map(evt => {
			const ftn = speakEvents[evt.name] || _.noop;
			return () => {
				ftn({args: evt.args, name: evt.name});
			};
		});

		_.each(toCall, e => {
			e();
		});
	});
};

// Catch all for some random steps that we shouldn't bother with in server-side
// rendering.
const doMiscInit = function(vault, updater, router) {
	URLHooks.setup(router);
	Bundles.setInitializeParams(vault, updater, router);

	const onNullContext = () => {
		CanvasUtil.removeNullContextListener(onNullContext);
		const ls = Storage.getStorage();
		ErrorUtil.log("E_NULL_CONTEXT", "Shortvas.get returned falsy", {
			wasConservingCanvasMemory: ls.getItem("conserveCanvasMemory"),
		});
		ls.setItem("conserveCanvasMemory", Date.now());

		vault.dispatch({
			type: AT.SHOW_MESSAGE,
			messageType: "nullContext",
			data: {
				refreshOnClose: true,
			},
		});
	};
	CanvasUtil.addNullContextListener(onNullContext);

	// Listening to local storage changes.
	// If the browser is bugged and fires these events on its own tab, just skip
	// this. Not much we can do. Because that test takes a bit of time, we wait
	// longer ourselves.
	setTimeout(() => {
		if (global.window && !Storage.firesEventOnOwnTab()) {
			global.window.addEventListener("storage", e =>
				vault.dispatch({
					type: AT.STORAGE_UPDATED,
					key: e.key,
					oldValue: e.oldValue,
					newValue: e.newValue,
				})
			);
		}
	}, 1000);
};

const setupUnloadListener = function(vault) {
	let lastUnload = -Infinity;
	// TODO: maybe make a hook system for this?
	window.onbeforeunload = () => {
		let message;
		const state = vault.getState();
		const userCR = atUser(state);
		const cart = atCart(state).get();
		const OpsCart = Bundles.requireOpt("office", "OpsCart");
		const result = atCurrResult(state).get();
		const OpsResult = Bundles.requireOpt("trainer", "OpsResult");
		if (userCR.getIn("submittingBugReport")) {
			message = BugCopy.onBeforeUnloadMessage;
		} else if (Storage.isDisabled() && OpsCart && !OpsCart.isEmpty(cart)) {
			// TODO: maybe only show this if on an office page?
			message =
				"Your cart has items and will be emptied if you leave. Are you sure you want to leave the page?";
		} else if (result && OpsResult && OpsResult.needsUnloadConfirm(result)) {
			message =
				"You will lose your progress on your current lesson. Are you sure you want to leave?";
		}
		// T17982: onbeforeunload gets fired rapidly sometimes, and the final vault
		// action can trigger api requests unconditionally. Suppress this dispatch
		// if it's within a second of the last unload call.
		const thisUnload = window.performance
			? window.performance.now()
			: Date.now();
		if (thisUnload > lastUnload + 1000) {
			flushErrorSaving();
			vault.dispatchNow({type: AT.FINAL}, {suppressErrors: true});
		}
		lastUnload = thisUnload;
		return message;
	};
};

const setupTouchListeners = function(vault) {
	//This touch class control uses the fact that touchstart always fires before mouseover.
	//It's based on code from http://www.javascriptkit.com/dhtmltutors/sticky-hover-issue-solutions.shtml
	let recentlyUsedTouch = false; //flag set when the user recently used touch
	let hasTouchClass = false; //whether the can-touch class is present
	let touchTimer = null; //timer to unset the recentlyUsedTouch flag

	//Set the recent touch flag and add the touch class if necessary
	const onTouchStart = function() {
		clearTimeout(touchTimer);
		recentlyUsedTouch = true;
		if (!hasTouchClass) {
			hasTouchClass = true;
			document.documentElement.classList.add("can-touch");
		}
		//Wait 500ms before unsetting the touch flag to cover the time when the next mouseover fires
		touchTimer = setTimeout(() => (recentlyUsedTouch = false), 500);
	};

	//Remove the touch class if not triggered by a touch event
	const onMouseOver = function() {
		if (!recentlyUsedTouch && hasTouchClass) {
			hasTouchClass = false;
			document.documentElement.classList.remove("can-touch");
		}
	};

	document.addEventListener("touchstart", onTouchStart, false);
	document.addEventListener("mouseover", onMouseOver, false);
};

// containerEl is not required, although browser rendering almost certainly needs it.
const getReactElementToMount = function(vault, router, containerEl) {
	return (
		<MainContainer
			containerEl={containerEl}
			vault={vault.getViewInterface()}
			router={router}
		/>
	);
};

// This should only be called in a browser.
const startReact = function() {
	if (process.env.IS_NODE) {
		throw new Error("Can't run main startReact function on server");
	}

	// Wire up the Bundle loading system. See comments on injectLoadBundle.
	Bundles.injectLoadBundle(loadBundle);

	const {vault, vaultLogger, updater, plugins, router} = createVault();
	setupDebugVars(vault, vaultLogger);
	setupErrorTrapping(vault, vaultLogger, plugins.api);
	setupVaultUpdate(vault, router, plugins.api, plugins.cookie);
	const isServerRendered = initVaultState(
		vault,
		global.window && global.window.location.pathname
	);
	doMiscInit(vault, updater, router);
	setupUnloadListener(vault);
	setupTouchListeners(vault);

	// BAEditor should not render React things.
	if (!global.PLAY_PROBLEM_DATA && global.IS_ON_BAEDITOR) {
		return;
	}

	const topContainer = document.getElementById("top_container");
	topContainer.classList.add("ba-newtopsubmenu");

	// If the server sent both HTML (isServerRendered) and extra bundles
	// (BA_EXTRA_BUNDLES) then we should not hydrate React until the extra
	// bundles load. So in addition to domready, we have some other things to
	// wait on before starting React up.

	// TODO: util? CanvasImage also has cb-merging logic with a counter.

	const funcsToWaitOn = [domready];
	if (isServerRendered && Array.isArray(global.BA_EXTRA_BUNDLES)) {
		global.BA_EXTRA_BUNDLES.forEach(bundleName => {
			funcsToWaitOn.push(cb => Bundles.load(bundleName, cb));
		});
	} else if (global.PLAY_PROBLEM_DATA) {
		funcsToWaitOn.push(cb => Bundles.load("trainer", cb));
	}
	const finalCb = function() {
		ReactModal2.getApplicationElement = () => topContainer;
		WindowResize.setTopSizeGetter(() => {
			const clientRect = topContainer.getBoundingClientRect();
			if (shouldRenderLeftSideNavBar(vault.state.page)) {
				clientRect.width -= LEFT_SIDE_NAV_WIDTH;
			}
			return clientRect;
		});
		const toMount = getReactElementToMount(vault, router, topContainer);
		if (isServerRendered) {
			ReactDOM.hydrateRoot(topContainer, toMount);
		} else {
			topContainer.innerHTML = "";
			ReactDOM.createRoot(topContainer).render(toMount);
		}
	};
	let left = funcsToWaitOn.length;
	funcsToWaitOn.forEach(f => {
		f(() => {
			left -= 1;
			if (left === 0) finalCb();
		});
	});
};

module.exports = {
	createVault,
	setupDebugVars,
	setupErrorTrapping,
	setupVaultUpdate,
	initVaultState,
	doMiscInit,
	setupUnloadListener,
	setupTouchListeners,
	getReactElementToMount,
	startReact,
};
